import React from 'react';

/**
 * Function that prevents hanging prepositions and conjunctions at the end of lines.
 * Replaces single-letter and multi-letter conjunctions and prepositions with non-breaking connections to the next word.
 * 
 * Funkcja zapobiegająca wiszącym przyimkom i spójnikom na końcu linii.
 * Zamienia spójniki i przyimki jednoliterowe oraz wieloliterowe na nierozerwalne połączenia ze słowem następującym.
 * 
 * @param {string} text - Text to process / Tekst do przetworzenia
 * @returns {string} - Processed text without hanging conjunctions/prepositions / Przetworzony tekst bez wiszących spójników/przyimków
 */
export const preventOrphans = (text) => {
  if (!text) return '';
  
  // HTML non-breaking space / Nierozerwalna spacja HTML
  const nbsp = '\u00A0';

  // Regex for single-letter conjunctions/prepositions followed by a word
  // Regex dla jednoliterowych spójników/przyimków z następującym słowem
  const singleLetterRegex = /(\s)([aiouwz])(\s+)([^\s])/gi;
  let result = text.replace(singleLetterRegex, `$1$2${nbsp}$4`);
  
  // Regex for multi-letter conjunctions and prepositions
  // Regex dla spójników i przyimków wieloliterowych
  const wordsToPrevent = [
    'ale', 'aby', 'albo', 'bo', 'by', 'czy', 'dla', 'do', 'gdy', 'jak', 'jest', 
    'lub', 'na', 'nad', 'ni', 'od', 'po', 'pod', 'przy', 'oraz', 'we', 'ze', 
    'za', 'że', 'który', 'która', 'które', 'którzy'
  ];
  
  const multiLetterPattern = new RegExp(`(\\s)(${wordsToPrevent.join('|')})(\\s+)([^\\s])`, 'gi');
  result = result.replace(multiLetterPattern, `$1$2${nbsp}$4`);
  
  return result;
};

/**
 * React component that wraps text, protecting it from hanging conjunctions/prepositions
 * Komponent React, który opakowuje tekst zabezpieczając go przed wiszącymi spójnikami/przyimkami
 * 
 * @param {Object} props - Component properties / Właściwości komponentu
 * @param {string} props.text - Text to process / Tekst do przetworzenia
 * @param {string} props.component - Component name to render (default: span) / Nazwa komponentu do renderowania (domyślnie: span)
 * @param {Object} props.props - Additional properties to pass to the component / Dodatkowe właściwości do przekazania do komponentu
 * @returns {React.Element} - React element with secured text / Element React z zabezpieczonym tekstem
 */
export const NoOrphans = ({ text, component = 'span', ...props }) => {
  if (!text) return null;
  
  const Component = component;
  const processedText = preventOrphans(text);
  
  return <Component {...props} dangerouslySetInnerHTML={{ __html: processedText }} />;
};

export default NoOrphans; 